import { useCallback, useMemo, useState } from "react";
import { ReportSettingsSpot } from "@common/helpers/report-settings/explain-report-setting-spot";
import { ReportSettingsTarget } from "@common/helpers/report-settings/report-setting-candidates";
import {
    ReportSettingKey,
    ReportSettingsStoredRow,
    StoredReportSettingValues,
} from "@common/helpers/report-settings/report-setting-fields";
import {
    useSaveWorkspaceReportSettingsMutation,
    useWorkspaceReportSettings,
} from "@api/reportSettings.api";
import {
    ReportSettingsDraft,
    ReportSettingsDraftChange,
    ReportSettingsDraftSpot,
    buildSaveChanges,
    clearSavedFields,
    hasUnsavedEdits,
    hasUnsavedEditsForSpot,
    hasUnsavedEditsForTarget,
    setDraftField,
} from "./reportSettingsDrafts";

const NO_ROWS: ReportSettingsStoredRow[] = [];

interface FieldEditInput<K extends ReportSettingKey> {
    spot: ReportSettingsDraftSpot;
    key: K;
    value: StoredReportSettingValues[K];
}

interface DraftsSource {
    rows: ReportSettingsStoredRow[] | undefined;
    save: (changes: ReportSettingsDraftChange[]) => Promise<void>;
    isSaving: boolean;
}

export const useDraftsFor = ({
    rows: loadedRows,
    save,
    isSaving,
}: DraftsSource) => {
    const rows = loadedRows ?? NO_ROWS;
    const [drafts, setDrafts] = useState<ReportSettingsDraft[]>([]);

    const setField = useCallback(
        <K extends ReportSettingKey>({ spot, key, value }: FieldEditInput<K>) =>
            setDrafts((current) =>
                setDraftField({ drafts: current, spot, key, value }),
            ),
        [],
    );

    const resetField = useCallback(
        ({ spot, key }: Omit<FieldEditInput<ReportSettingKey>, "value">) =>
            setDrafts((current) =>
                setDraftField({ drafts: current, spot, key, value: null }),
            ),
        [],
    );

    const checks = useMemo(
        () => ({
            hasUnsavedEdits: () => hasUnsavedEdits({ rows, drafts }),
            hasUnsavedEditsForTarget: (target: ReportSettingsTarget) =>
                hasUnsavedEditsForTarget({ rows, drafts, target }),
            hasUnsavedEditsForSpot: (spot: ReportSettingsSpot) =>
                hasUnsavedEditsForSpot({ rows, drafts, spot }),
        }),
        [rows, drafts],
    );

    const saveAll = useCallback(async (): Promise<boolean> => {
        const changes = buildSaveChanges({ rows, drafts });
        if (changes.length === 0) {
            return false;
        }
        await save(changes);
        setDrafts((current) =>
            clearSavedFields({ drafts: current, savedDrafts: drafts }),
        );
        return true;
    }, [rows, drafts, save]);

    return {
        rows,
        drafts,
        setField,
        resetField,
        ...checks,
        saveAll,
        isSaving,
    };
};

export const useReportSettingsDrafts = ({
    workspaceId,
}: {
    workspaceId: string;
}) => {
    const { data } = useWorkspaceReportSettings(workspaceId);
    const { mutateAsync, isPending } =
        useSaveWorkspaceReportSettingsMutation(workspaceId);
    const save = useCallback(
        (changes: ReportSettingsDraftChange[]) =>
            mutateAsync({
                changes: changes.flatMap(({ owner, ...change }) =>
                    owner.kind === "template" ? [] : [{ owner, ...change }],
                ),
            }),
        [mutateAsync],
    );
    return useDraftsFor({ rows: data?.rows, save, isSaving: isPending });
};
